/**
 * Native wishlist feature (no wishlist app installed). Items are stored as an
 * array of product handles in localStorage and shared across the site via the
 * `wishlist:change` document event.
 */
(function () {
  var STORAGE_KEY = 'om_wishlist_handles';

  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var handles = raw ? JSON.parse(raw) : [];
      return Array.isArray(handles) ? handles : [];
    } catch (error) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      // Storage unavailable (private browsing, quota, etc.) — fail silently.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: handles } }));
  }

  var OMWishlist = {
    get: readHandles,
    has: function (handle) {
      return readHandles().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var handles = readHandles();
      if (handles.indexOf(handle) === -1) {
        handles.push(handle);
        writeHandles(handles);
      }
    },
    remove: function (handle) {
      var handles = readHandles().filter(function (item) {
        return item !== handle;
      });
      writeHandles(handles);
    },
    toggle: function (handle) {
      if (OMWishlist.has(handle)) {
        OMWishlist.remove(handle);
        return false;
      }
      OMWishlist.add(handle);
      return true;
    },
  };

  window.OMWishlist = OMWishlist;

  function updateToggleButtons() {
    var handles = readHandles();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (button) {
      var handle = button.getAttribute('data-wishlist-handle');
      var active = handles.indexOf(handle) !== -1;
      var label = active
        ? button.getAttribute('data-remove-label')
        : button.getAttribute('data-add-label');

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (label) button.setAttribute('aria-label', label);

      var text = button.querySelector('.wishlist-button__text');
      if (text && label) text.textContent = label;
    });
  }

  function refreshUI() {
    updateToggleButtons();
  }

  document.addEventListener('wishlist:change', refreshUI);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();
    var handle = button.getAttribute('data-wishlist-handle');
    if (!handle) return;

    var added = OMWishlist.toggle(handle);
    if (added) {
      var clearPop = function () {
        button.classList.remove('wishlist-button--pop');
      };
      clearPop();
      void button.offsetWidth; // restart the animation if it's still mid-pop from a rapid re-click
      button.classList.add('wishlist-button--pop');
      button.addEventListener('animationend', clearPop, { once: true });
      // Fallback in case animationend never fires (e.g. prefers-reduced-motion), so
      // the class can't get stuck — harmless no-op if animationend already cleared it.
      setTimeout(clearPop, 500);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshUI);
  } else {
    refreshUI();
  }

  /* Wishlist page — fetches each saved product and renders it into the grid. */
  if (!window.customElements || customElements.get('wishlist-items')) return;

  function moneyFormat(cents) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  customElements.define(
    'wishlist-items',
    class extends HTMLElement {
      connectedCallback() {
        this.showPrice = this.dataset.showPrice === 'true';
        this.render();
        this.onWishlistChange = this.render.bind(this);
        document.addEventListener('wishlist:change', this.onWishlistChange);
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this.onWishlistChange);
      }

      render() {
        var handles = OMWishlist.get();

        if (!handles.length) {
          this.renderEmpty();
          return;
        }

        var root = (window.routes && window.routes.root_url) || '/';
        var self = this;

        Promise.all(
          handles.map(function (handle) {
            return fetch(root.replace(/\/$/, '') + '/products/' + handle + '.js')
              .then(function (response) {
                return response.ok ? response.json() : null;
              })
              .catch(function () {
                return null;
              });
          })
        ).then(function (results) {
          var products = results.filter(Boolean);
          var foundHandles = products.map(function (product) {
            return product.handle;
          });

          handles
            .filter(function (handle) {
              return foundHandles.indexOf(handle) === -1;
            })
            .forEach(function (handle) {
              OMWishlist.remove(handle);
            });

          if (!products.length) {
            self.renderEmpty();
            return;
          }

          self.innerHTML = '';

          var wrapper = document.createElement('div');
          wrapper.className = 'wishlist-table-wrapper';

          var table = document.createElement('div');
          table.className = 'wishlist-table';
          table.setAttribute('role', 'table');
          table.appendChild(self.buildHeaderRow());
          products.forEach(function (product) {
            table.appendChild(self.buildRow(product));
          });
          wrapper.appendChild(table);
          self.appendChild(wrapper);
          self.appendChild(self.buildShareLink());
        });
      }

      buildHeaderRow() {
        var row = document.createElement('div');
        row.className = 'wishlist-table__header';
        row.setAttribute('role', 'row');

        var columns = [
          ['wishlist-table__cell--image', this.dataset.columnImage || 'Image'],
          ['wishlist-table__cell--product', this.dataset.columnProduct || 'Product Name'],
          ['wishlist-table__cell--price', this.dataset.columnPrice || 'Price'],
          ['wishlist-table__cell--add', this.dataset.columnAdd || 'Add'],
          ['wishlist-table__cell--remove', this.dataset.columnRemove || 'Remove'],
        ];

        columns.forEach(function (column) {
          if (column[0] === 'wishlist-table__cell--price' && this.showPrice === false) return;
          var cell = document.createElement('span');
          cell.className = 'wishlist-table__cell ' + column[0];
          cell.textContent = column[1];
          row.appendChild(cell);
        }, this);

        return row;
      }

      buildRow(product) {
        var variant = product.variants[0] || {};

        var row = document.createElement('div');
        row.className = 'wishlist-table__row';
        row.setAttribute('role', 'row');

        var imageCell = document.createElement('span');
        imageCell.className = 'wishlist-table__cell wishlist-table__cell--image';
        var link = document.createElement('a');
        link.href = product.url;
        link.className = 'wishlist-table__image';
        if (product.featured_image) {
          var img = document.createElement('img');
          img.src = product.featured_image;
          img.alt = product.title;
          img.loading = 'lazy';
          link.appendChild(img);
        }
        imageCell.appendChild(link);
        row.appendChild(imageCell);

        var productCell = document.createElement('span');
        productCell.className = 'wishlist-table__cell wishlist-table__cell--product';
        var title = document.createElement('a');
        title.href = product.url;
        title.className = 'wishlist-table__title';
        title.textContent = product.title;
        productCell.appendChild(title);
        if (product.vendor) {
          var vendor = document.createElement('span');
          vendor.className = 'wishlist-table__vendor';
          vendor.textContent = product.vendor;
          productCell.appendChild(vendor);
        }
        row.appendChild(productCell);

        if (this.showPrice) {
          var priceCell = document.createElement('span');
          priceCell.className = 'wishlist-table__cell wishlist-table__cell--price';
          priceCell.textContent = moneyFormat(variant.price);
          row.appendChild(priceCell);
        }

        var addCell = document.createElement('span');
        addCell.className = 'wishlist-table__cell wishlist-table__cell--add';
        if (product.available) {
          var addButton = document.createElement('button');
          addButton.type = 'button';
          addButton.className = 'wishlist-table__add';
          addButton.textContent = this.dataset.addToCartLabel || 'Add to cart';
          addButton.addEventListener('click', this.addToCart.bind(this, variant.id, addButton));
          addCell.appendChild(addButton);
        } else {
          var soldOut = document.createElement('span');
          soldOut.className = 'wishlist-table__sold-out';
          soldOut.textContent = this.dataset.soldOutLabel || 'Sold out';
          addCell.appendChild(soldOut);
        }
        row.appendChild(addCell);

        var removeCell = document.createElement('span');
        removeCell.className = 'wishlist-table__cell wishlist-table__cell--remove';
        var removeButton = document.createElement('button');
        removeButton.type = 'button';
        removeButton.className = 'wishlist-table__remove';
        removeButton.setAttribute('aria-label', this.dataset.removeLabel || 'Remove from wishlist');
        removeButton.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/></svg>';
        removeButton.addEventListener('click', function () {
          OMWishlist.remove(product.handle);
        });
        removeCell.appendChild(removeButton);
        row.appendChild(removeCell);

        return row;
      }

      buildShareLink() {
        var link = document.createElement('a');
        link.className = 'wishlist-page__share link underlined-link';
        link.href =
          'mailto:?subject=' +
          encodeURIComponent(document.title) +
          '&body=' +
          encodeURIComponent(this.dataset.pageUrl || window.location.href);
        link.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path stroke="currentColor" stroke-width="1.2" d="M2 5h16v10H2V5Zm0 0 8 6 8-6"/></svg><span>' +
          (this.dataset.shareLabel || 'Share my Wishlist Via Email') +
          '</span>';
        return link;
      }

      addToCart(variantId, button) {
        if (!variantId || !window.routes || !window.routes.cart_add_url) return;

        var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cart.getSectionsToRender().map(function (section) {
              return section.id;
            })
          );
          formData.append('sections_url', window.location.pathname);
        }

        button.disabled = true;

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: formData,
        })
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            if (response.status) return;

            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist', cartData: response });
            }

            button.textContent = this.dataset.addedToCartLabel || 'Added';
          }.bind(this))
          .catch(function () {})
          .finally(function () {
            button.disabled = false;
          });
      }

      renderEmpty() {
        this.innerHTML = '';

        var wrap = document.createElement('div');
        wrap.className = 'wishlist-grid__empty';

        var heading = document.createElement('p');
        heading.className = 'wishlist-grid__empty-heading h2';
        heading.textContent = this.dataset.emptyHeading || 'Your wishlist is empty';
        wrap.appendChild(heading);

        var body = document.createElement('p');
        body.className = 'wishlist-grid__empty-body';
        body.textContent = this.dataset.emptyBody || '';
        wrap.appendChild(body);

        if (this.dataset.continueShoppingUrl) {
          var link = document.createElement('a');
          link.href = this.dataset.continueShoppingUrl;
          link.className = 'button button--primary';
          link.textContent = this.dataset.continueShoppingText || 'Continue shopping';
          wrap.appendChild(link);
        }

        this.appendChild(wrap);
      }
    }
  );
})();
